import { Menu, Tooltip } from '@mantine/core';
import cn from 'classnames';
import * as React from 'react';

import { FORMATTERS, HEADING_FORMATTERS } from '../config';
import type { FormatType } from '../types';

import { buildTooltipLabel } from './utils';

import s from './FormattingToolbar.module.scss';

type FormattingToolbarProps = {
  formatters: FormatType[];
  onClick: (type: FormatType) => void;
  disabled?: boolean;
  isActive?: (type: FormatType) => boolean;
};

const FormattingToolbar: React.FC<FormattingToolbarProps> = ({ formatters, onClick, disabled = false, isActive }) => {
  return (
    <>
      {formatters.map((type) => {
        const config = FORMATTERS[type];

        if (!config) {
          return null;
        }

        const tooltipLabel = buildTooltipLabel(config.aria, config.shortcut);

        if (type === 'heading') {
          return (
            <Menu key={type} withinPortal={false} disabled={disabled}>
              <Menu.Target>
                <Tooltip
                  label={tooltipLabel}
                  withArrow
                  withinPortal={false}
                  disabled={disabled}
                  openDelay={150}
                  events={{ hover: true, focus: false, touch: false }}
                >
                  <button
                    type="button"
                    className={cn(
                      s['root__toolbar-button'],
                      s[`root__toolbar-button_${type}`],
                      isActive?.(type) && s['root__toolbar-button_active'],
                      disabled && s['root__toolbar-button_disabled'],
                    )}
                    aria-label={config.aria}
                    disabled={disabled}
                  >
                    <span className={s['root__toolbar-label']}>{config.renderIcon({ size: 16 })}</span>
                  </button>
                </Tooltip>
              </Menu.Target>
              <Menu.Dropdown>
                {HEADING_FORMATTERS.map((headingType) => {
                  const headingConfig = FORMATTERS[headingType];

                  if (!headingConfig) {
                    return null;
                  }

                  return (
                    <Menu.Item
                      key={headingType}
                      className={cn(s['root__menu-item'], isActive?.(headingType) && s['root__menu-item_active'])}
                      disabled={disabled}
                      // eslint-disable-next-line react/jsx-no-bind
                      onClick={() => onClick(headingType)}
                      leftSection={
                        <span className={s['root__menu-icon']}>{headingConfig.renderIcon({ size: 16 })}</span>
                      }
                    >
                      {headingConfig.aria}
                    </Menu.Item>
                  );
                })}
              </Menu.Dropdown>
            </Menu>
          );
        }

        return (
          <Tooltip
            key={type}
            label={tooltipLabel}
            withArrow
            withinPortal={false}
            disabled={disabled}
            openDelay={150}
            events={{ hover: true, focus: false, touch: false }}
          >
            <button
              type="button"
              className={cn(
                s['root__toolbar-button'],
                s[`root__toolbar-button_${type}`],
                isActive?.(type) && s['root__toolbar-button_active'],
                disabled && s['root__toolbar-button_disabled'],
              )}
              onClick={() => onClick(type)}
              aria-label={config.aria}
              disabled={disabled}
            >
              <span className={s['root__toolbar-label']}>{config.renderIcon({ size: 16 })}</span>
            </button>
          </Tooltip>
        );
      })}
    </>
  );
};

export default React.memo(FormattingToolbar);
